/**
 * Imagen 1080x1920 capturada con html2canvas.
 * Renderiza la cancha con los jugadores posicionados según los x/y de formations.js,
 * igual que PitchView pero en coordenadas absolutas para la imagen de share.
 */
import React from "react";
import placeholder from "../../../assets/player_placeholder.png";
import { CANVAS_CONFIG, AVATAR_DIAMETER, STYLE_DEFAULTS } from "./constants";
import { getPlayerPhoto } from "../functions";

function buildPitch(offsetTop) {
  const top = offsetTop ?? CANVAS_CONFIG.defaultPitchTop;
  const { left, right, bottom } = CANVAS_CONFIG.pitchMargins;
  const { width, height } = CANVAS_CONFIG;

  return {
    top,
    left,
    right,
    bottom,
    w: width - left - right,
    h: height - top - bottom,
  };
}

const getRootStyle = (shareImage) => ({
  width: `${CANVAS_CONFIG.width}px`,
  height: `${CANVAS_CONFIG.height}px`,
  position: "relative",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  boxSizing: "border-box",
  overflow: "hidden",
  fontFamily: "Arial, sans-serif",
  ...(shareImage
    ? { backgroundImage: `url(${shareImage})` }
    : { backgroundColor: "#1a1a2e" }),
});

// Convierte x/y porcentual del slot a px absolutos dentro del canvas
function slotToPx(slot, pitch) {
  const cx = pitch.left + (slot.x / 100) * pitch.w;
  const cy = pitch.top + (slot.y / 100) * pitch.h;
  return { cx, cy };
}

// ============================================================================
// FUNCIONES HELPER PARA ESTILOS
// ============================================================================

/**
 * Genera estilos para el contenedor del pin del jugador
 */
function getPlayerPinStyle(cx, cy, diameter) {
  return {
    position: "absolute",
    left: `${cx}px`,
    top: `${cy}px`,
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0px",
    width: `${diameter + 40}px`,
  };
}

/**
 * Genera estilos para el avatar del jugador
 */
function getAvatarStyle(diameter, hasPlayer) {
  return {
    ...STYLE_DEFAULTS.avatar,
    width: `${diameter}px`,
    height: `${diameter}px`,
    border: `2px solid ${hasPlayer ? "#ffffff" : "rgba(255,255,255,0.5)"}`,
  };
}

/**
 * Genera estilos para el contenedor del nombre
 */
function getNameContainerStyle() {
  return STYLE_DEFAULTS.nameContainer;
}

/**
 * Genera estilos para el span del nombre
 */
function getNameSpanStyle(color) {
  return {
    padding: "4px 0px 4px 10px",
    color: color || "#1a1a1a",
    display: "block",
    whiteSpace: "nowrap",
  };
}

/**
 * Genera estilos para el span del apellido
 */
function getLastNameSpanStyle(color) {
  return {
    padding: "4px 10px 4px 10px",
    color: color || "#27793a",
    display: "block",
    whiteSpace: "nowrap",
  };
}

/**
 * Genera estilos para el span de slot vacío
 */
function getEmptySlotStyle() {
  return {
    color: "rgba(255,255,255,0.6)",
  };
}

// Un jugador posicionado en la cancha
const PlayerPin = React.memo(function PlayerPin({ slot, player, accentColor, nameColor, lastNameColor, pitch, selectionPath }) {
  const { cx, cy } = slotToPx(slot, pitch);
  const hasPlayer = !!player;

  return (
    <div style={getPlayerPinStyle(cx, cy, AVATAR_DIAMETER)}>
      {/* Avatar */}
      <div style={getAvatarStyle(AVATAR_DIAMETER, hasPlayer)}>
        <img
          src={player?.photo ? getPlayerPhoto(player.photo, selectionPath) : placeholder}
          alt={player ? `${player.firstName} ${player.lastName}` : slot.label}
          style={STYLE_DEFAULTS.image}
          onError={(e) => { e.target.src = placeholder; }}
        />
      </div>

      {/* Nombre */}
      <div style={getNameContainerStyle()}>
        {hasPlayer ? (
          <>
            <span style={getNameSpanStyle(nameColor)}>
              {player.firstName?.[0]}.
            </span>
            <span style={getLastNameSpanStyle(lastNameColor)}>
              {player.lastName?.split(" ")[0]}
            </span>
          </>
        ) : (
          <span style={getEmptySlotStyle()}>
            {slot.label}
          </span>
        )}
      </div>
    </div>
  );
});

export default function ShareLineupImage({ slots, assignments, config }) {
  const shareImage = config?.shareImage;
  const accentColor = config?.accentColor || "#e74c3c";
  const nameColor = config?.nameColor || "#ffffff";
  const lastNameColor = config?.lastNameColor || "#ffffff";
  const selectionPath = config?.selectionPath;

  // pitchOffsetTop permite ajustar la posición vertical de los jugadores
  // para que calcen con la imagen de fondo de cada equipo
  const pitch = buildPitch(config?.pitchOffsetTop);

  const rootStyle = shareImage
    ? { ...getRootStyle(shareImage), backgroundImage: `url(${shareImage})` }
    : getRootStyle(null);

  return (
    <div aria-hidden="true" style={rootStyle}>
      {/* Jugadores posicionados directamente sobre la imagen de fondo */}
      {slots.map((slot) => (
        <PlayerPin {...{ slot, accentColor, nameColor, lastNameColor, pitch, selectionPath }}
          key={slot.id}
          player={assignments[slot.id] || null}
        />
      ))}
    </div>
  );
}